/**
 * A plan's entry, stop and targets as lines on a chart — derived, never stored.
 *
 * This is the one place the plan doc calls out as "the one the obvious
 * implementation gets wrong", so it is worth being explicit about what is *not*
 * happening here.
 *
 * ## Why these are not annotations
 *
 * Minting three `hline` `Annotation`s when a plan is saved is easy, and it
 * immediately gives one number two homes. Edit the stop and the orphaned line
 * still renders — correctly drawn, and wrong. Delete the plan and the lines
 * outlive it. Load the workspace on another device and they disagree with the
 * journal.
 *
 * So the chart derives its levels at render time from the plans for the symbol
 * it is showing, and **`workspace.schema.json` needs no change at all**. It is
 * the same split `signals.md` makes when it refuses to let the server send a
 * `y`: the model carries facts, and the rendering is computed from them.
 *
 * ## What a level knows
 *
 * Price, what it is, and its distance in percent and R — because a stop is only
 * meaningful next to what it costs, and a target only next to what it pays. R
 * comes from `plan-math`, so it measures from the frozen stop once there is one
 * and cannot drift from what the plan list shows.
 */

import type { Plan } from "@qufolio/model";

import { rMultiple, riskEntry } from "./plan-math";

/** What a level *is*, which decides how it draws. */
export type LevelKind = "entry" | "stop" | "target";

export interface PlanLevel {
  planId: string;
  kind: LevelKind;
  /** The target's id, so a drag knows which one it moved. Absent otherwise. */
  targetId?: string;
  price: number;
  /** `Stop`, `T1`, `T2`… — short enough to sit on a line. */
  label: string;
  /** Distance from the entry, as a percentage of it. */
  percent: number;
  /** Distance in R. Zero at the entry by definition. */
  r: number;
  /**
   * Whether this level may be dragged.
   *
   * The entry cannot, once frozen: a fill is a fact rather than an opinion, and
   * a chart that let you drag it would be offering an edit the server refuses.
   * Better to not offer it than to explain it afterwards.
   */
  draggable: boolean;
}

/**
 * One level moved, as a whole plan ready to save.
 *
 * **A pre-freeze drag is an edit, not an amendment**, and the two go to
 * different endpoints. Before the first fill there is nothing to amend
 * *against*, so `POST /journal/plan/{id}/amend` refuses outright — the store
 * says "a plan that has not frozen is edited, not amended" and means it. The
 * edit path is the ordinary upsert, which is also what the form and the
 * right-click target editor already use.
 *
 * So this exists to turn "the stop moved to 16.40" into the argument that call
 * takes. Pure, and here rather than in the handler, because `App.tsx` reaches
 * SciChart and cannot be rendered in a test — this is the part that can be.
 *
 * A target is matched **by id**, never by position: `normalise_shares` may
 * reorder them server-side, and an index would then move a different target
 * than the one under the cursor. An id that matches nothing changes nothing,
 * which is the right answer for a line whose target was deleted in another tab.
 */
export function withLevel(
  plan: Plan,
  level: { kind: string; targetId?: string },
  price: number,
): Plan {
  if (level.kind === "entry") return { ...plan, entry: price };
  if (level.kind === "stop") return { ...plan, stop: price };
  if (level.kind !== "target" || !level.targetId) return plan;
  return {
    ...plan,
    targets: (plan.targets ?? []).map((target) =>
      target.id === level.targetId ? { ...target, price } : target,
    ),
  };
}

/** A theme token per kind, so a level cannot carry a hex (D13). */
export const LEVEL_TOKENS: Record<LevelKind, string> = {
  entry: "textSecondary",
  stop: "negative",
  target: "positive",
};

/**
 * Every level one plan draws, in price order from the stop up.
 *
 * A `closed` or `cancelled` plan draws nothing: its levels are history, and
 * leaving them on the chart would make a finished trade look like a live one.
 * A `missed` plan draws nothing for the same reason — the price went without
 * it, and the lines would suggest it is still waiting.
 */
export function planLevels(plan: Plan): PlanLevel[] {
  if (plan.status === "closed" || plan.status === "cancelled" || plan.status === "missed") {
    return [];
  }

  const entry = riskEntry(plan);
  const frozen = plan.planned != null;
  const at = (price: number): { percent: number; r: number } => ({
    percent: entry > 0 ? ((price - entry) / entry) * 100 : 0,
    r: rMultiple(plan, price),
  });

  const levels: PlanLevel[] = [
    {
      planId: plan.id,
      kind: "entry",
      price: plan.entry,
      label: "Entry",
      ...at(plan.entry),
      draggable: !frozen,
    },
    {
      planId: plan.id,
      kind: "stop",
      price: plan.stop,
      label: "Stop",
      ...at(plan.stop),
      // Always draggable, including after the fill: managing a trade *is*
      // moving the stop. Past the freeze the drag becomes an amendment rather
      // than an edit, which is the caller's business and not the line's.
      draggable: true,
    },
  ];

  (plan.targets ?? []).forEach((target, index) => {
    levels.push({
      planId: plan.id,
      kind: "target",
      targetId: target.id,
      price: target.price,
      label: `T${index + 1}`,
      ...at(target.price),
      draggable: true,
    });
  });

  return levels.sort((a, b) => a.price - b.price);
}

/**
 * The levels for every plan on the symbol a chart is showing.
 *
 * Filtered here rather than by the caller so "which plans does this chart draw"
 * has one answer. Case-folded because a symbol reaches this from a chart group,
 * a URL and a form, and only one of those is guaranteed to have uppercased it.
 */
export function levelsForSymbol(plans: readonly Plan[], symbol: string): PlanLevel[] {
  const wanted = symbol.toUpperCase();
  return plans.filter((plan) => plan.symbol.toUpperCase() === wanted).flatMap(planLevels);
}

/**
 * How a level reads beside its line: price, then what it costs or pays.
 *
 * R is omitted at the entry, where it is zero by construction and would be
 * noise. Percent is signed, because "−9.1%" and "9.1%" are different facts and
 * a stop is always the first of them.
 */
export function levelLabel(level: PlanLevel): string {
  const price = level.price.toFixed(2);
  if (level.kind === "entry") return `${level.label}${GAP}${price}`;
  const percent = `${level.percent >= 0 ? "+" : ""}${level.percent.toFixed(1)}%`;
  const r = `${level.r >= 0 ? "+" : ""}${level.r.toFixed(2)}R`;
  return `${level.label}${GAP}${price} · ${percent} · ${r}`;
}

/**
 * The gap between what a level *is* and what it *costs*.
 *
 * `T1` and `201.00` are two different kinds of thing — a name and a number —
 * and one space ran them together into something that read as a single token.
 * The dots already separate the three numbers from each other; this separates
 * the name from all of them.
 *
 * Plain spaces rather than an en or em quad: the label is drawn by SciChart's
 * WebGL text engine against a font atlas, and a character the atlas does not
 * carry renders as a box — which is a worse gap than none.
 */
const GAP = "   ";

/**
 * The fills, as marks on the chart — the second thing the journal draws.
 *
 * Derived like the levels above and for the same reason: an execution is
 * already a row, and minting an annotation beside it would give one fill two
 * homes. Correct a typo'd price and the stored mark would still sit at the old
 * one, correctly drawn and wrong.
 *
 * A mark carries its own side, because that is what decides the glyph and the
 * colour, and its quantity, because scaling out is the case the chart most
 * needs to show — three sells at three prices is the shape of a plan working.
 */
export interface ExecutionMark {
  executionId: string;
  /** Unix seconds, matching the chart's category axis timestamps. */
  time: number;
  price: number;
  side: "buy" | "sell";
  quantity: number;
  /** A theme token, never a hex (D13). */
  token: string;
  /** `▲` under the bar for a buy, `▼` over it for a sell. */
  glyph: string;
  label: string;
}

/**
 * Marks for the fills of one symbol.
 *
 * Takes trades as well as executions because an execution names only its trade,
 * and the symbol lives on the trade — filtering here rather than at the call
 * site keeps "which fills does this chart show" a single answer, exactly as
 * `levelsForSymbol` does.
 *
 * A simulated fill is marked the same as a real one *on the chart* and told
 * apart in the journal. The chart is asking "where did this position change",
 * and the answer does not differ; a statistics page asking "what did this
 * strategy make" is where the distinction has to bite.
 */
export function executionMarks(
  executions: readonly { id: string; tradeId: string; side: string; quantity: number; price: number; executedAt: string }[],
  trades: readonly { id: string; symbol: string }[],
  symbol: string,
): ExecutionMark[] {
  const wanted = symbol.toUpperCase();
  const mine = new Set(
    trades.filter((t) => t.symbol.toUpperCase() === wanted).map((t) => t.id),
  );
  return executions
    .filter((e) => mine.has(e.tradeId))
    .map((e) => {
      const buy = e.side === "buy";
      return {
        executionId: e.id,
        // Seconds, because that is what the transport decodes an Arrow
        // timestamp column into and what the category axis is indexed by.
        time: Math.floor(new Date(e.executedAt).getTime() / 1000),
        price: e.price,
        side: buy ? "buy" : "sell",
        quantity: e.quantity,
        token: buy ? "positive" : "negative",
        glyph: buy ? "▲" : "▼",
        label: `${buy ? "Buy" : "Sell"} ${e.quantity} @ ${e.price.toFixed(2)}`,
      } satisfies ExecutionMark;
    })
    .sort((a, b) => a.time - b.time);
}
